import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Sparkles, ArrowLeft, ArrowRight, Building, FileText, Lock, PlayCircle, LayoutDashboard
} from 'lucide-react';

// =========================
// DEMO CREDENTIALS
// =========================
const DEMO_ACCOUNTS = {
  buyer: { email: 'buyer.demo@procureoneultra.com', password: 'Demo@123', org: 'Green Heights Co-operative Housing Society' },
  supplier: { email: 'supplier.demo@procureoneultra.com', password: 'Demo@123', org: 'Prime Facility Services Pvt. Ltd.' },
};

// =========================
// MAIN COMPONENT
// =========================
const Demo = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loggingIn, setLoggingIn] = useState(null);

  const quickLogin = async (role) => {
    setLoggingIn(role);
    try {
      // Best-effort logout first to clear any stale cookies from a previous session
      try {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      } catch { /* ignore */ }

      const creds = role === 'buyer' ? DEMO_ACCOUNTS.buyer : DEMO_ACCOUNTS.supplier;
      const data = await login(creds.email, creds.password);
      if (data?.email) {
        toast.success(`Logged in as Demo ${role === 'buyer' ? 'Buyer' : 'Supplier'}`);
        // Give the browser a brief moment to persist the new cookie before the next route fetches data
        setTimeout(() => {
          navigate(role === 'buyer' ? '/buyer-dashboard' : '/supplier-dashboard');
        }, 200);
      } else {
        toast.error('Demo login failed — please try again');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Demo login failed');
    } finally { setLoggingIn(null); }
  };

  return (
    <div className="min-h-screen bg-background">
      <DemoBanner />
      <header className="bg-navy text-white">
        <nav className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-2">
          <button onClick={() => navigate('/')} className="text-white/70 hover:text-white inline-flex items-center" data-testid="demo-back-btn">
            <ArrowLeft className="h-5 w-5 mr-2" /> Back
          </button>
          <div className="flex items-center space-x-2">
            <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-gold" />
            <div className="text-base sm:text-lg font-bold">ProcureOne Ultra · Demo Mode</div>
          </div>
          <div className="text-xs text-white/60 hidden sm:block">Procurement Lifecycle · Real Dashboards</div>
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-14 max-w-5xl">
        {/* Intro */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-block px-3 py-1 rounded-full bg-orange/15 text-orange text-xs font-bold uppercase tracking-wider mb-3">Live Demo</div>
          <h1 className="text-2xl sm:text-4xl font-bold text-navy mb-3">Experience ProcureOne Ultra Without Signing Up</h1>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
            See the complete real-world procurement journey from start to finish — or jump straight into a live dashboard with pre-loaded data.
          </p>
        </div>

        {/* =========================
            PRIMARY — LIFECYCLE SIMULATION
        ========================= */}
        <section className="mb-10" data-testid="demo-lifecycle-section">
          <div className="flex items-center mb-3">
            <PlayCircle className="h-5 w-5 sm:h-6 sm:w-6 text-orange mr-2" />
            <h2 className="text-lg sm:text-xl font-bold text-navy">Procurement Lifecycle Simulation</h2>
            <span className="ml-3 px-2 py-0.5 rounded-full bg-orange text-white text-[10px] font-bold uppercase">Recommended</span>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mb-5">
            Walk through a complete real-world procurement journey — from creating a requirement, getting internal committee approvals, publishing tender, supplier clarifications, comparative statements, award and purchase order — all the way to contract closure.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            {/* Buyer Lifecycle */}
            <button
              onClick={() => navigate('/demo/lifecycle/buyer')}
              className="text-left p-5 sm:p-7 rounded-lg border-2 border-navy/20 hover:border-navy bg-gradient-to-br from-white to-navy/5 hover:shadow-lg transition-all group"
              data-testid="demo-lifecycle-buyer-btn"
            >
              <div className="inline-block p-3 rounded-lg bg-navy text-white mb-4">
                <Building className="h-6 w-6 sm:h-7 sm:w-7" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-navy mb-1 group-hover:text-orange transition-colors">Buyer Procurement Lifecycle</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3">Housing Societies · Commercial Complexes · Corporates</p>
              <ul className="text-xs text-foreground/70 space-y-1 mb-4 list-disc list-inside">
                <li>12-step end-to-end journey</li>
                <li>Internal committee approvals</li>
                <li>Clarifications · Comparative statement</li>
                <li>Award · Purchase Order · Closure</li>
              </ul>
              <div className="inline-flex items-center text-sm font-semibold text-navy group-hover:text-orange">
                Start Lifecycle Demo <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>

            {/* Supplier Lifecycle */}
            <button
              onClick={() => navigate('/demo/lifecycle/supplier')}
              className="text-left p-5 sm:p-7 rounded-lg border-2 border-orange/30 hover:border-orange bg-gradient-to-br from-white to-orange/5 hover:shadow-lg transition-all group"
              data-testid="demo-lifecycle-supplier-btn"
            >
              <div className="inline-block p-3 rounded-lg bg-orange text-white mb-4">
                <FileText className="h-6 w-6 sm:h-7 sm:w-7" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-navy mb-1 group-hover:text-orange transition-colors">Supplier Procurement Lifecycle</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3">Service Providers · EPC Contractors · Material Suppliers</p>
              <ul className="text-xs text-foreground/70 space-y-1 mb-4 list-disc list-inside">
                <li>10-step end-to-end journey</li>
                <li>Tender discovery · Free document downloads</li>
                <li>Clarifications · Quotation submission</li>
                <li>Award notification · PO · Final payment</li>
              </ul>
              <div className="inline-flex items-center text-sm font-semibold text-orange group-hover:text-navy">
                Start Lifecycle Demo <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          </div>
        </section>

        {/* Divider */}
        <div className="flex items-center mb-8" aria-hidden="true">
          <div className="flex-1 h-px bg-border" />
          <div className="px-3 text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">or</div>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* =========================
            SECONDARY — EXPLORE REAL DASHBOARDS
        ========================= */}
        <section className="mb-10" data-testid="demo-dashboards-section">
          <div className="flex items-center mb-3">
            <LayoutDashboard className="h-5 w-5 text-navy mr-2" />
            <h2 className="text-base sm:text-lg font-bold text-navy">Explore Real Dashboards</h2>
            <span className="ml-3 px-2 py-0.5 rounded-full bg-navy text-white text-[10px] font-bold uppercase">Hands-On</span>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mb-4">
            Already understand the workflow? Log into the actual dashboard with pre-loaded tenders, quotations and awards — click around exactly like a paying customer.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <button
              onClick={() => quickLogin('buyer')}
              disabled={loggingIn !== null}
              className="p-4 rounded-md bg-card border border-border hover:border-navy text-navy transition-all text-left disabled:opacity-50"
              data-testid="live-demo-buyer-btn"
            >
              <div className="flex items-center mb-1">
                <Building className="h-4 w-4 mr-2" />
                <div className="font-bold text-sm">Demo Buyer Dashboard</div>
              </div>
              <div className="text-xs opacity-80 mb-1">{DEMO_ACCOUNTS.buyer.org}</div>
              <div className="text-[11px] opacity-60">{loggingIn === 'buyer' ? 'Logging in…' : '4 tenders · 3 quotations · 1 awarded · 1 closed'}</div>
            </button>
            <button
              onClick={() => quickLogin('supplier')}
              disabled={loggingIn !== null}
              className="p-4 rounded-md bg-card border border-border hover:border-orange text-orange transition-all text-left disabled:opacity-50"
              data-testid="live-demo-supplier-btn"
            >
              <div className="flex items-center mb-1">
                <FileText className="h-4 w-4 mr-2" />
                <div className="font-bold text-sm">Demo Supplier Dashboard</div>
              </div>
              <div className="text-xs opacity-80 mb-1">{DEMO_ACCOUNTS.supplier.org}</div>
              <div className="text-[11px] opacity-60">{loggingIn === 'supplier' ? 'Logging in…' : '1 saved · 2 participated · 1 awarded · ★ 5.0'}</div>
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3 italic">Demo data is isolated from production. Resets automatically.</p>
        </section>

        {/* Demo credentials reference */}
        <div className="bg-card border border-border rounded-lg p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <Lock className="h-5 w-5 text-orange flex-shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm text-muted-foreground">
              <strong className="text-navy">Prefer to login manually?</strong> Use these demo credentials at the{' '}
              <button onClick={() => navigate('/login')} className="text-navy font-semibold underline">login page</button>:
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="bg-muted/40 p-2 rounded font-mono text-[11px]">
                  <div>Buyer: <strong>buyer.demo@procureoneultra.com</strong></div>
                  <div>Password: <strong>Demo@123</strong></div>
                </div>
                <div className="bg-muted/40 p-2 rounded font-mono text-[11px]">
                  <div>Supplier: <strong>supplier.demo@procureoneultra.com</strong></div>
                  <div>Password: <strong>Demo@123</strong></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== SUB COMPONENTS ==============
const DemoBanner = () => (
  <div className="bg-orange text-white text-center py-1.5 sm:py-2 text-xs sm:text-sm font-semibold uppercase tracking-wider" data-testid="demo-banner">
    Demo Mode · Sample Data Only · No Real Registrations, Payments or Notifications
  </div>
);

export default Demo;
